import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
    getClasses, 
    getClassDetail as getClassDetailApi, 
    createClass as createClassApi, 
    updateClass as updateClassApi, 
    deleteClass as deleteClassApi,
    addStudentToClass as addStudentToClassApi,
    removeStudentFromClass as removeStudentFromClassApi,
    updateStudentClassRole as updateStudentClassRoleApi
} from '../../apis/classes';
import { RootState } from '../store';

export interface Class {
    id: string;
    name: string;
    description?: string;
    banner_url?: string;
    logo_url?: string;
    teacher_id: string;
    teacher?: {
        id: string;
        full_name: string;
        email: string;
        profile_picture_url?: string;
        phone_number?: string;
        username?: string;
        teacher_type?: string;
    };
    student_count?: number;
    organization_id?: string | null;
    organization_name?: string | null;
    created_at: string;
    updated_at?: string | null;
    deleted_at?: string | null;
    created_by_id?: string | null;
    updated_by_id?: string | null;
    status?: string;
    students?: any[];
    shared_count?: number;
    shared_role?: string | null;
    shares?: any[];
    tuition_fee?: number | null;
    payment_due_day?: number | null;
}

interface ClassState {
    classes: Class[];
    currentClass: Class | null;
    total: number;
    page: number;
    size: number;
    pages: number;
    my_count: number;
    shared_count: number;
    loading: boolean;
    error: string | null;
}

const initialState: ClassState = {
    classes: [],
    currentClass: null,
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    my_count: 0,
    shared_count: 0,
    loading: false,
    error: null,
};

// Thunks
export const fetchClasses = createAsyncThunk(
    'classes/fetchClasses',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getClasses(params);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchClassDetail = createAsyncThunk(
    'classes/fetchClassDetail',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await getClassDetailApi(id);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const addClass = createAsyncThunk(
    'classes/addClass',
    async (data: Partial<Class>, { rejectWithValue }) => {
        try {
            const response = await createClassApi(data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const editClass = createAsyncThunk(
    'classes/editClass',
    async ({ id, data }: { id: string; data: Partial<Class> }, { rejectWithValue }) => {
        try {
            const response = await updateClassApi(id, data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const removeClass = createAsyncThunk(
    'classes/removeClass',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteClassApi(id);
            return id;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const assignStudentToClass = createAsyncThunk(
    'classes/assignStudentToClass',
    async ({ classId, studentId }: { classId: string; studentId: string }, { rejectWithValue, dispatch }) => {
        try {
            await addStudentToClassApi(classId, studentId);
            dispatch(fetchClassDetail(classId));
            return { classId, studentId };
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const unassignStudentFromClass = createAsyncThunk(
    'classes/unassignStudentFromClass',
    async ({ classId, studentId }: { classId: string; studentId: string }, { rejectWithValue, dispatch }) => {
        try {
            await removeStudentFromClassApi(classId, studentId);
            dispatch(fetchClassDetail(classId));
            return { classId, studentId };
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updateStudentRole = createAsyncThunk(
    'classes/updateStudentRole',
    async ({ classId, studentId, role }: { classId: string; studentId: string; role: string }, { rejectWithValue, dispatch }) => {
        try {
            await updateStudentClassRoleApi(classId, studentId, role);
            dispatch(fetchClassDetail(classId));
            return { classId, studentId, role };
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const classSlice = createSlice({
    name: 'classes',
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        clearCurrentClass: (state) => {
            state.currentClass = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClasses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClasses.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.classes = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
                state.my_count = action.payload.my_count ?? 0;
                state.shared_count = action.payload.shared_count ?? 0;
            })
            .addCase(fetchClasses.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.detail || 'Error fetching classes';
            })
            .addCase(fetchClassDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClassDetail.fulfilled, (state, action: PayloadAction<Class>) => {
                state.loading = false;
                state.currentClass = action.payload;
            })
            .addCase(fetchClassDetail.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload?.detail || 'Không tìm thấy lớp học';
                state.currentClass = null;
            })
            .addCase(addClass.fulfilled, (state, action: PayloadAction<Class>) => {
                state.classes.unshift(action.payload);
                state.total += 1;
            })
            .addCase(editClass.fulfilled, (state, action: PayloadAction<Class>) => {
                state.currentClass = action.payload;
                const index = state.classes.findIndex(c => c.id === action.payload.id);
                if (index !== -1) state.classes[index] = action.payload;
            })
            .addCase(removeClass.fulfilled, (state, action: PayloadAction<string>) => {
                state.classes = state.classes.filter(c => c.id !== action.payload);
                state.total -= 1;
            });
    },
});

export const { setPage, clearCurrentClass } = classSlice.actions;

export const selectAllClasses = (state: RootState) => state.classes.classes;
export const selectCurrentClass = (state: RootState) => state.classes.currentClass;

export default classSlice.reducer;
